import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, MessageCircle, Radar } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errCode, formatXAF } from "@/lib/api";
import { useRealtime, useRealtimeEvent } from "@/lib/realtime";
import { useI18n } from "@/i18n/I18nContext";
import { useQueryClient } from "@tanstack/react-query";
import { PushPrompt } from "@/components/PushPrompt";

const fmtElapsed = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const Seats = ({ filled, total }) => (
  <div className="flex justify-center gap-2" data-testid="queue-seats">
    {Array.from({ length: total }).map((_, i) => (
      <span key={i} className={`h-3 w-8 rounded-full transition-colors duration-500 ${i < filled ? "bg-amber-400" : "bg-white/10"}`} />
    ))}
  </div>
);

const TimeoutPrompt = ({ open, onContinue, onQuit }) => {
  const { t } = useI18n();
  return (
    <Dialog open={open}>
      <DialogContent className="rounded-2xl border-white/10 bg-[#121620] sm:max-w-sm [&>button]:hidden" data-testid="queue-timeout-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("queue.promptTitle")}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">{t("queue.promptText")}</p>
        <div className="grid grid-cols-2 gap-3 pt-2">
          <button onClick={onQuit} className="h-12 rounded-full border border-white/15 font-semibold" data-testid="queue-prompt-quit-btn">{t("queue.quit")}</button>
          <button onClick={onContinue} className="h-12 rounded-full bg-amber-500 font-semibold text-[#0B0D12]" data-testid="queue-prompt-continue-btn">{t("queue.continue")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RoomShare = ({ code }) => {
  const { t } = useI18n();
  const link = `${window.location.origin}/lobby?code=${code}`;
  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: "BKMPLAY", text: t("room.shareText", { code }), url: link });
      else {
        await navigator.clipboard.writeText(link);
        toast.success(t("room.copied"));
      }
    } catch {
      /* share cancelled */
    }
  };
  return (
    <div className="surface space-y-3 p-5" data-testid="room-share">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{t("room.code")}</p>
      <p className="font-mono text-3xl font-semibold tracking-[0.3em] text-amber-300" data-testid="room-code">{code}</p>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${t("room.shareText", { code })} ${link}`)}`} target="_blank" rel="noreferrer"
        className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#25D366] font-semibold text-[#0B0D12]" data-testid="room-whatsapp-btn">
        <MessageCircle className="h-5 w-5" /> {t("room.whatsapp")}
      </a>
      <button onClick={share} className="h-12 w-full rounded-full border border-amber-500/40 font-semibold text-amber-300" data-testid="room-share-btn">{t("room.share")}</button>
      <PushPrompt />
    </div>
  );
};

export default function QueuePage() {
  const { t, tErr, lang } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { counts } = useRealtime();
  const [queue, setQueue] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [prompt, setPrompt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [roomWaiting, setRoomWaiting] = useState(null);

  useEffect(() => {
    api.get("/play/active").then(({ data }) => {
      if (data.kind === "game") navigate(`/game/${data.game_id}`, { replace: true });
      else if (data.kind === "queue") {
        setQueue(data.queue);
        setPrompt(!!data.queue.prompt_deadline);
      } else navigate("/lobby", { replace: true });
    });
    const id = setInterval(() => setNow(Date.now()), 1000);
    // Fallback when the live connection dropped (e.g. player left the app to share the code on WhatsApp).
    const check = () => api.get("/play/active").then(({ data }) => {
      if (data.kind === "game") navigate(`/game/${data.game_id}`, { replace: true });
    }).catch(() => {});
    const poll = setInterval(check, 3000);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); clearInterval(poll); document.removeEventListener("visibilitychange", onVis); };
  }, [navigate]);

  useRealtimeEvent((m) => {
    if (m.type === "room_update") setRoomWaiting(m.waiting);
    if (m.type === "match_found") navigate(`/game/${m.game_id}`);
    if (m.type === "game_state" && ["starting", "in_progress"].includes(m.game.status)) navigate(`/game/${m.game.id}`);
    if (m.type === "queue_timeout_prompt") setPrompt(true);
    if (m.type === "queue_left") {
      toast.info(t(`queue.left_${m.reason}`));
      qc.invalidateQueries({ queryKey: ["wallet"] });
      navigate("/lobby");
    }
  });

  const leave = async () => {
    setBusy(true);
    try {
      await api.post("/queue/leave");
      toast.success(t("queue.leftOk"));
      qc.invalidateQueries({ queryKey: ["wallet"] });
      navigate("/lobby");
    } catch (e) {
      toast.error(tErr(errCode(e)));
      navigate("/lobby");
    } finally {
      setBusy(false);
    }
  };
  const keep = async () => {
    setPrompt(false);
    await api.post("/queue/continue").catch(() => {});
  };

  if (!queue) return <div className="py-24 text-center text-slate-500">{t("common.loading")}</div>;
  const waiting = queue.room_code ? (roomWaiting ?? queue.waiting) : (counts[`${queue.player_count}:${queue.stake}`] ?? queue.waiting);
  const elapsed = Math.max(0, Math.floor((now - Date.parse(queue.joined_at)) / 1000));
  return (
    <div className="mx-auto max-w-md space-y-8 py-6 text-center rise" data-testid="queue-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">{t("queue.title")}</p>
        <h1 className="mt-3 font-display text-2xl font-extrabold sm:text-3xl" data-testid="queue-combo">
          {t("queue.subtitle", { n: queue.player_count, stake: formatXAF(queue.stake, lang) })}
        </h1>
      </div>
      <div className="relative mx-auto grid h-52 w-52 place-items-center">
        <span className="absolute inset-0 animate-ping rounded-full border border-amber-500/30" />
        <span className="absolute inset-6 animate-pulse rounded-full border border-amber-500/20" />
        <div className="pulse-ring grid h-28 w-28 place-items-center rounded-full bg-amber-500/15">
          <Radar className="h-12 w-12 animate-spin text-amber-400" style={{ animationDuration: "3s" }} />
        </div>
      </div>
      <div className="space-y-3">
        <p className="font-mono text-4xl font-semibold" data-testid="queue-counter">{waiting}/{queue.player_count}</p>
        <Seats filled={waiting} total={queue.player_count} />
        <p className="text-sm text-slate-400">{t("queue.elapsed")} : <span className="font-mono text-slate-200" data-testid="queue-elapsed">{fmtElapsed(elapsed)}</span></p>
        <p className="text-xs text-slate-500">{t("queue.locked")} : <span className="font-mono">{formatXAF(queue.stake, lang)}</span></p>
      </div>
      {queue.room_code && <RoomShare code={queue.room_code} />}
      <button onClick={leave} disabled={busy} data-testid="queue-leave-btn"
        className="flex h-14 w-full items-center justify-center gap-2 rounded-full border border-red-500/30 font-semibold text-red-300 transition-colors hover:bg-red-500/10 disabled:opacity-60">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogOut className="h-5 w-5" />} {t(queue.is_creator ? "room.cancel" : "queue.leave")}
      </button>
      <TimeoutPrompt open={prompt} onContinue={keep} onQuit={leave} />
    </div>
  );
}
